import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getApiUrl } from "../config/api";
import api, { API_BASE_URL } from "../config/api";
import "../App.css";

const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, "-");

function Home() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPages, setFilteredPages] = useState([]);
  const [pageDisplayLimits, setPageDisplayLimits] = useState({});
  const [homepageBlogs, setHomepageBlogs] = useState([]);
  const searchResultsRef = useRef(null);

  useEffect(() => {
    const fetchPagesAndContents = async () => {
      setLoading(true);
      const pagesApiUrl = getApiUrl("pages");
      console.log("Fetching pages from:", pagesApiUrl);

      try {
        const response = await fetch(pagesApiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        console.log("Pages response status:", response.status);

        if (!response.ok) {
          throw new Error(`Failed to load pages. Status: ${response.status}`);
        }

        const pagesJson = await response.json();
        const pagesList = Array.isArray(pagesJson) ? pagesJson : [];

        const contentsResults = await Promise.all(
          pagesList.map(async (page) => {
            if (!page || !page.slug) {
              return [];
            }

            const contentsUrl = getApiUrl(`pages/${page.slug}/contents`);
            try {
              const contentsResponse = await fetch(contentsUrl, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
              });

              if (!contentsResponse.ok) {
                console.warn(
                  `Failed to fetch contents for page ${page.slug}. Status: ${contentsResponse.status}`
                );
                return [];
              }

              const contentsJson = await contentsResponse.json();
              const rawContents = Array.isArray(contentsJson.contents)
                ? contentsJson.contents
                : [];
              return rawContents.map((item) => ({
                ...item,
                slug: item?.slug || slugify(item?.title || ""),
              }));
            } catch (err) {
              console.error(
                `Error fetching contents for page ${page?.slug}:`,
                err
              );
              return [];
            }
          })
        );

        const combinedPages = pagesList
          .map((page, index) => {
            if (!page) return null;
            return {
              ...page,
              contents: contentsResults[index] || [],
            };
          })
          .filter(Boolean);

        setPages(combinedPages);
        setFilteredPages(combinedPages);

        const limits = {};
        combinedPages.forEach((page) => {
          if (page && page.id) {
            limits[page.id] = 20;
          }
        });
        setPageDisplayLimits(limits);
      } catch (error) {
        console.error("Error fetching pages or contents:", error);
        setPages([]);
        setFilteredPages([]);
        setPageDisplayLimits({});
      } finally {
        setLoading(false);
      }
    };

    fetchPagesAndContents();
  }, []);

  // Fetch homepage blogs (max 3)
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await api.get("/blogs?page=1");
        if (response.data.success) {
          // Get only first 3 blogs
          const blogs = response.data.data.data.slice(0, 3);
          setHomepageBlogs(blogs);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };
    fetchBlogs();
  }, []);

  // Icons array for different page types
  const getPageIcon = (index) => {
    const icons = [
      "fas fa-briefcase",
      "fas fa-chart-line",
      "fas fa-id-card",
      "fas fa-book",
      "fas fa-file-alt",
      "fas fa-graduation-cap",
      "fas fa-trophy",
      "fas fa-certificate",
    ];
    return icons[index % icons.length];
  };

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === "") {
      setFilteredPages(pages);
    } else {
      const filtered = pages.reduce((acc, page) => {
        if (!page) return acc;

        const pageTitle = (page.title || "").toLowerCase();
        const pageContents = Array.isArray(page.contents) ? page.contents : [];
        const matchesTitle = pageTitle.includes(query);
        const matchingContents = pageContents.filter((content) =>
          (content?.title || "").toLowerCase().includes(query)
        );

        if (matchesTitle || matchingContents.length > 0) {
          acc.push({
            ...page,
            contents: matchesTitle ? pageContents : matchingContents,
          });
        }

        return acc;
      }, []);
      setFilteredPages(filtered);
    }
  };

  // Handle View More for a specific page
  const handleViewMore = (pageId) => {
    setPageDisplayLimits((prev) => ({
      ...prev,
      [pageId]: (prev[pageId] || 20) + 20,
    }));
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && searchQuery && filteredPages.length > 0) {
      e.preventDefault();
      setTimeout(() => {
        if (searchResultsRef.current) {
          searchResultsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  // Scroll to results when search changes
  useEffect(() => {
    if (searchQuery && filteredPages.length > 0 && searchResultsRef.current) {
      setTimeout(() => {
        searchResultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [searchQuery, filteredPages]);

  return (
    <>
      {/* Search Bar */}
      <div class="onesignal-customlink-container"></div>
      <section className="search-section">
        <div className="container">
          <div className="page-search-container">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search here..."
                value={searchQuery}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setFilteredPages(pages);
                    // Reset display limits
                    const limits = {};
                    pages.forEach((page) => {
                      if (page && page.id) {
                        limits[page.id] = 20;
                      }
                    });
                    setPageDisplayLimits(limits);
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1>Your Gateway to Government Jobs & Career Success</h1>
            <p>
              Find the latest government job opportunities, check exam results,
              download admit cards, and enhance your skills with our
              comprehensive courses.
            </p>
            <div className="hero-buttons">
              {pages.length > 0 && (
                <>
                  {pages[0].link &&
                  (pages[0].link.startsWith("http://") ||
                    pages[0].link.startsWith("https://")) ? (
                    <a
                      href={pages[0].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Browse {pages[0].title}
                    </a>
                  ) : (
                    <Link
                      to={pages[0].link || `/${pages[0].slug || ""}`}
                      className="btn btn-primary"
                    >
                      Browse {pages[0].title}
                    </Link>
                  )}
                  {pages.length > 1 &&
                    (pages[1].link &&
                    (pages[1].link.startsWith("http://") ||
                      pages[1].link.startsWith("https://")) ? (
                      <a
                        href={pages[1].link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline"
                      >
                        View {pages[1].title}
                      </a>
                    ) : (
                      <Link
                        to={pages[1].link || `/${pages[1].slug || ""}`}
                        className="btn btn-outline"
                      >
                        View {pages[1].title}
                      </Link>
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="updates-section" ref={searchResultsRef}>
        <div className="container">
          <h2 className="section-title">
            <i className="fas fa-clock"></i>
            Latest Updates
          </h2>
          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <p>Loading...</p>
            </div>
          ) : filteredPages.length > 0 ? (
            <div className="updates-list-by-page">
              {filteredPages.map((page, pageIndex) => {
                if (!page || !page.id || !page.title) return null;

                const pageSlug =
                  page.slug || page.title.toLowerCase().replace(/\s+/g, "-");
                const pagePath = page.link || `/${pageSlug}`;
                const displayLimit = pageDisplayLimits[page.id] || 20;
                const pageContents =
                  page.contents && Array.isArray(page.contents)
                    ? page.contents
                    : [];
                const visibleContents = pageContents.slice(0, displayLimit);
                const hasMore = pageContents.length > displayLimit;

                return (
                  <div key={page.id} className="page-updates-group">
                    <div className="page-updates-header">
                      <i className={getPageIcon(pageIndex)}></i>
                      <h3>{page.title}</h3>
                    </div>

                    {visibleContents.length > 0 ? (
                      <>
                        <ul className="updates-list">
                          {visibleContents.map((content, contentIndex) => {
                            if (!content || !content.id || !content.title)
                              return null;

                            // Check link_type: if 'content', link to content display page
                            // if 'direct', link to external URL
                            const linkType = content.link_type || "content";
                            const isContentPage = linkType === "content";
                            const isDirectLink =
                              linkType === "direct" && content.link;
                            const contentSlug =
                              content.slug || slugify(content.title);

                            return (
                              <li key={content.id} className="update-list-item">
                                {isContentPage ? (
                                  <Link
                                    to={`/${pageSlug}/${contentSlug}`}
                                    state={{ contentId: content.id }}
                                    className="update-link"
                                  >
                                    {content.title}
                                  </Link>
                                ) : isDirectLink ? (
                                  <a
                                    href={content.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="update-link"
                                  >
                                    {content.title}
                                  </a>
                                ) : (
                                  <span
                                    className="update-link"
                                    style={{ cursor: "default" }}
                                  >
                                    {content.title}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>

                        {hasMore && (
                          <div
                            style={{
                              textAlign: "center",
                              marginTop: "1.5rem",
                              marginBottom: "2rem",
                            }}
                          >
                            <button
                              className="btn btn-primary"
                              onClick={() => handleViewMore(page.id)}
                            >
                              View More
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "1rem",
                          color: "#666",
                        }}
                      >
                        <p>No updates available for {page.title}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : searchQuery ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <p>No results found for "{searchQuery}"</p>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <p>No updates available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Blog Section */}
      {homepageBlogs.length > 0 && (
        <section className="industry-insights padding-first pharmaxim-blog industry-insights-home">
          <div className="container">
            {/* Header */}
            <div
              className="insights-header text-center mb-5 featured-news-row"
              style={{ textAlign: "center", marginBottom: "30px" }}
            >
              <h1
                className="display-5 fw-bold"
                style={{ marginBottom: "30px", textAlign: "center" }}
              >
                <span style={{ color: "red" }}>सोच</span>
                <span style={{ color: "green", margin: "0 10px" }}>•</span>
                <span style={{ color: "green" }}>सूचना</span>
                <span style={{ color: "green", margin: "0 10px" }}>•</span>
                <span style={{ color: "orange" }}>समझ</span>
              </h1>
              <Link
                to="/blogs"
                className="btn-demo hover reveal d-inline-block"
                style={{
                  backgroundColor: "#ff9800", // Vibrant orange (modern & premium feel)
                  color: "#ffffff",
                  padding: "14px 36px", // Good size & comfortable click area
                  fontSize: "1rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                  borderRadius: "50px", // Fully rounded pill shape
                  textDecoration: "none",
                  display: "inline-block",
                  transition: "all 0.35s ease",
                  boxShadow: "0 6px 20px rgba(255, 152, 0, 0.3)", // Soft shadow for depth
                  border: "none",
                  outline: "none",
                  marginBottom: "20px", // As per your requirement
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e68900"; // Darker orange on hover
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 30px rgba(255, 152, 0, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ff9800";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(255, 152, 0, 0.3)";
                }}
              >
                View All
              </Link>
            </div>

            {/* Blog Grid */}
            <div
              className="blog-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "40px",
                maxWidth: "1400px",
                margin: "0 auto",
              }}
            >
              {homepageBlogs.map((blog) => (
                <div key={blog.id} style={{ height: "100%" }}>
                  {/* Insight Card */}
                  <div
                    className="insight-card h-100"
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                      transition: "all 0.35s ease",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-12px)";
                      e.currentTarget.style.boxShadow =
                        "0 18px 40px rgba(0,0,0,0.16)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 25px rgba(0,0,0,0.1)";
                    }}
                  >
                    {/* Image Section */}
                    <Link to={`/blog/${blog.slug}`}>
                      <div
                        className="card-image"
                        style={{
                          position: "relative",
                          height: "260px",
                          overflow: "hidden",
                        }}
                      >
                        <div className="image-placeholder zoom-in">
                          {blog.featured_image ? (
                            <img
                              src={`${API_BASE_URL}/storage/${blog.featured_image}`}
                              alt={blog.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.5s ease",
                                display: "block",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform =
                                  "scale(1.08)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/600x260/eeeeee/999999?text=No+Image";
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#aaa",
                                fontSize: "18px",
                              }}
                            >
                              No Image Available
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>

                    {/* Card Content */}
                    <div
                      className="card-content d-flex flex-column"
                      style={{
                        padding: "28px 26px",
                        flexGrow: 1,
                      }}
                    >
                      {/* Title */}
                      <Link
                        to={`/blog/${blog.slug}`}
                        style={{ textDecoration: "none" }}
                      >
                        <h3
                          className="card-headline"
                          style={{
                            margin: "0 0 14px 0",
                            fontSize: "1.35rem",
                            fontWeight: "600",
                            lineHeight: "1.3",
                            color: "#222",
                            transition: "color 0.3s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#ff9800")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#222")
                          }
                        >
                          {blog.title}
                        </h3>
                      </Link>

                      {/* Date */}

                      <div
                        className="card-date"
                        style={{
                          color: "#666",
                          fontSize: "14px",
                          marginBottom: "20px",
                        }}
                      >
                        {new Date(blog.published_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </div>

                      {/* Read More Button - Always at bottom */}
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="read-more-btn hover mt-auto align-self-start"
                        style={{
                          color: "#ff9800",
                          fontWeight: "600",
                          fontSize: "15px",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          textDecoration: "none",
                          transition: "all 0.3s ease",
                          display: "inline-block",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "translateX(10px)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "translateX(0)")
                        }
                      >
                        Read More →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Responsive Grid */}
            <style jsx>{`
              @media (max-width: 992px) {
                .blog-grid {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }
              @media (max-width: 768px) {
                .blog-grid {
                  grid-template-columns: 1fr !important;
                  gap: 32px;
                }
              }
            `}</style>
          </div>
        </section>
      )}

      {/* WhatsApp Join Section */}
      <section className="whatsapp-section">
        <div className="container">
          <div className="whatsapp-box">
            <div className="whatsapp-icon">
              <i className="fab fa-whatsapp"></i>
            </div>
            <div className="whatsapp-content">
              <h3>WhatsApp Join</h3>
              <p>Stay updated with latest job notifications on WhatsApp</p>
            </div>
            <div className="whatsapp-button">
              <a
                href="https://whatsapp.com/channel/0029Vb6DMPn2kNFiBHlkIw1Q"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <i className="fab fa-whatsapp"></i> Join Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
